import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  DecimalOnlyDirective,
  PhoneOnlyDirective,
} from '../core/directives';
import { EquipmentService } from '../core/services/equipment.service';
import { InventoryService } from '../core/services/inventory.service';
import { ExportService } from '../core/services/export.service';
import { MaintenanceService } from '../core/services/maintenance.service';
import {
  DamagedEquipmentRecord,
  EquipmentAsset,
} from '../core/models/equipment.models';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../core/utils/format.util';
import { confirmAction } from '../core/utils/confirm.util';
import { matchesItemQuery } from '../core/utils/item-filter.util';
import { maintenanceProblemFromDamage } from '../core/utils/maintenance-description.util';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-damaged',
  standalone: true,
  imports: [FormsModule, TitleCasePipe, DecimalOnlyDirective, PhoneOnlyDirective],
  templateUrl: './damaged.component.html',
  styleUrls: ['./damaged.component.css'],
})
export class DamagedComponent implements OnInit {
  private readonly equipmentService = inject(EquipmentService);
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly exporter = inject(ExportService);

  readonly allAssets = this.equipmentService.allAssets;
  readonly assetNameFilter = signal('');
  readonly filteredAssets = computed(() => {
    const query = this.assetNameFilter();
    const selectedId = this.formEquipment();
    return this.allAssets().filter(
      (asset) =>
        asset.id === selectedId ||
        matchesItemQuery(query, asset.name, asset.assetId),
    );
  });

  readonly timestamp = currentTimestamp();
  readonly formatDate = formatDate;
  readonly formatCurrency = formatCurrency;

  readonly searchQuery = signal('');
  readonly typeFilter = signal('');
  readonly statusFilter = signal('');

  readonly damagedStats = computed(() => {
    const list = this.equipmentService.allDamagedRecords();
    return {
      total: list.length,
      open: list.filter(
        (row) => row.status === 'open' || row.status === 'under-review',
      ).length,
      damaged: list.filter((row) => row.damageType === 'damaged').length,
      lost: list.filter((row) => row.damageType === 'lost').length,
    };
  });

  readonly filteredRecords = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const type = this.typeFilter();
    const status = this.statusFilter();

    return this.equipmentService.allDamagedRecords().filter((record) => {
      if (type && record.damageType !== type) {
        return false;
      }
      if (status && record.status !== status) {
        return false;
      }
      if (search) {
        const haystack = [
          record.reportNumber,
          record.equipment,
          record.itemCode,
          record.assetNumber,
          record.checkoutNumber,
          record.damageDescription,
          record.branchName,
          record.destination,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }
      return true;
    });
  });

  readonly showReportModal = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly isEditing = computed(() => this.editingId() !== null);
  readonly showViewModal = signal(false);
  readonly showRepairModal = signal(false);
  readonly showWriteOffModal = signal(false);

  readonly selectedRecord = signal<DamagedEquipmentRecord | null>(null);

  readonly formEquipment = signal('');
  readonly serialOptions = signal<Array<{ id: string; serialNumber: string }>>([]);
  readonly serialsLoading = signal(false);
  readonly selectedSerial = signal('');
  private readonly inventory = inject(InventoryService);
  readonly formDamageType = signal<'damaged' | 'lost'>('damaged');
  readonly formReportedDate = signal(new Date().toISOString().slice(0, 10));
  readonly formEstimatedCost = signal(0);
  readonly formStatus = signal<DamagedEquipmentRecord['status']>('open');

  readonly formAssignedTo = signal('');
  readonly formAssignedPhone = signal('');
  readonly formRepairNotes = signal('');
  readonly sendingToMaintenance = signal(false);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.equipmentService.refreshFromApi();
    }
  }

  findAsset(record: DamagedEquipmentRecord): EquipmentAsset | undefined {
    if (record.equipmentAssetID) {
      const byAssetId = this.equipmentService
        .allAssets()
        .find((row) => Number(row.id) === record.equipmentAssetID);
      if (byAssetId) {
        return byAssetId;
      }
    }

    return this.equipmentService
      .allAssets()
      .find((row) => row.name === record.equipment || row.id === record.id);
  }

  openReportModal(): void {
    this.editingId.set(null);
    this.assetNameFilter.set('');
    this.formEquipment.set(this.equipmentService.allAssets()[0]?.id ?? '');
    this.formDamageType.set('damaged');
    this.formReportedDate.set(new Date().toISOString().slice(0, 10));
    this.formEstimatedCost.set(0);
    this.formStatus.set('open');
    this.showReportModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  openEditModal(record: DamagedEquipmentRecord): void {
    this.editingId.set(record.id);
    const matched = this.findAsset(record);
    this.formEquipment.set(matched?.id ?? record.id);
    this.formDamageType.set(record.damageType);
    this.formReportedDate.set(record.reportedDate);
    this.formEstimatedCost.set(record.estimatedCost);
    this.formStatus.set(record.status);
    this.showReportModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeReportModal(): void {
    this.showReportModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  openViewModal(record: DamagedEquipmentRecord): void {
    this.selectedRecord.set(record);
    this.showViewModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeViewModal(): void {
    this.showViewModal.set(false);
    this.selectedRecord.set(null);
    document.body.style.overflow = 'auto';
  }

  openRepairModal(record: DamagedEquipmentRecord): void {
    this.selectedRecord.set(record);
    this.showRepairModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeRepairModal(): void {
    this.showRepairModal.set(false);
    document.body.style.overflow = 'auto';
  }

  openWriteOffModal(record: DamagedEquipmentRecord): void {
    this.selectedRecord.set(record);
    this.showWriteOffModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeWriteOffModal(): void {
    this.showWriteOffModal.set(false);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeReportModal();
      this.closeViewModal();
      this.closeRepairModal();
      this.closeWriteOffModal();
    }
  }

  onDamageItemChange(equipmentId: string): void {
    this.formEquipment.set(equipmentId);
    this.selectedSerial.set('');
    this.serialOptions.set([]);
    const asset = this.allAssets().find((row) => row.id === equipmentId);
    if (!asset || !this.equipmentService.isQuantityBasedEquipment(asset)) {
      return;
    }
    const itemId = Number(asset.inventoryItemID) || 0;
    if (!itemId) {
      return;
    }
    this.serialsLoading.set(true);
    this.inventory.fetchItemHistory(itemId).subscribe({
      next: (result) => {
        if (this.formEquipment() !== equipmentId) {
          return;
        }
        const options = (result.data?.assets ?? []).flatMap((row) => {
          const serial = (row.serialNumber || '').trim();
          if (!serial || /^AST-\d+/i.test(serial)) {
            return [];
          }
          const status = (row.status || 'AVAILABLE').trim().toUpperCase();
          if (['ISSUED', 'CHECKED_OUT', 'IN_REPAIR', 'IN_TRANSIT', 'DAMAGED', 'RETIRED'].includes(status)) {
            return [];
          }
          return [{ id: row.id, serialNumber: serial }];
        });
        this.serialOptions.set(options);
        this.serialsLoading.set(false);
      },
      error: () => this.serialsLoading.set(false),
    });
  }

  submitReport(): void {
    const equipmentId = this.formEquipment();
    if (!equipmentId) {
      this.showToastMessage('Please select an item', 'error');
      return;
    }
    if (this.serialOptions().length && !this.selectedSerial()) {
      this.showToastMessage('Select the serial number for this item.', 'error');
      return;
    }

    const asset = this.allAssets().find((row) => row.id === equipmentId);
    const chosenSerial = this.serialOptions().find(
      (row) => row.serialNumber === this.selectedSerial(),
    );
    const payload = {
      equipment: chosenSerial
        ? `${asset?.name ?? equipmentId} (${chosenSerial.serialNumber})`
        : (asset?.name ?? equipmentId),
      reportedDate: this.formReportedDate(),
      damageType: this.formDamageType(),
      status: this.formStatus(),
      estimatedCost: this.formEstimatedCost(),
      equipmentAssetID: chosenSerial
        ? Number(chosenSerial.id) || 0
        : Number(asset?.id) || 0,
    };

    if (this.editingId()) {
      this.equipmentService
        .updateDamagedRecordViaApi(this.editingId()!, payload)
        .subscribe((result) => {
          if (result.success) {
            this.closeReportModal();
            this.showToastMessage('Report updated successfully', 'success');
          } else {
            this.showToastMessage(
              result.message ?? 'Failed to update report',
              'error',
            );
          }
        });
      return;
    }

    this.equipmentService
      .createDamageReportViaApi(payload)
      .subscribe((result) => {
        if (result.success) {
          this.closeReportModal();
          this.showToastMessage('Report submitted successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to submit report',
            'error',
          );
        }
      });
  }

  deleteRecord(record: DamagedEquipmentRecord): void {
    if (!confirmAction(`Cancel report "${record.reportNumber}"?`)) {
      return;
    }

    this.equipmentService
      .deleteDamagedRecordViaApi(record.id)
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage('Report cancelled successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to cancel report',
            'error',
          );
        }
      });
  }

  resolveRecord(record: DamagedEquipmentRecord): void {
    const qtyNote =
      (record.quantityDamaged ?? 0) > 0
        ? ` This will return ${record.quantityDamaged} repaired unit(s) to stock.`
        : '';
    if (
      !confirmAction(
        `Mark "${record.reportNumber}" as repaired / resolved?${qtyNote} It will be removed from Damaged / Lost.`,
      )
    ) {
      return;
    }

    this.equipmentService
      .resolveDamagedRecordViaApi(record.id, 'resolved')
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            'Repaired item removed from Damaged / Lost and restocked where applicable',
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to resolve report',
            'error',
          );
        }
      });
  }

  scheduleRepair(): void {
    const record = this.selectedRecord();
    if (!record) {
      this.showToastMessage('Select a report first', 'error');
      return;
    }

    const assignedTo = this.formAssignedTo().trim();
    if (!assignedTo) {
      this.showToastMessage(
        'Enter the name of the person responsible for the repair',
        'error',
      );
      return;
    }

    const asset = this.findAsset(record);
    const equipmentAssetID = record.equipmentAssetID ?? Number(asset?.id);
    const equipmentReturnID = Number(record.id);

    if (!Number.isFinite(equipmentReturnID) || equipmentReturnID <= 0) {
      this.showToastMessage(
        'Invalid damage report id. Refresh and try again.',
        'error',
      );
      return;
    }

    // Quantity rentals without a tagged asset: create maintenance job via return id.
    if (!Number.isFinite(equipmentAssetID) || equipmentAssetID <= 0) {
      this.sendingToMaintenance.set(true);
      this.maintenanceService
        .sendDamagedToMaintenance({
          equipmentReturnID,
          assignedToName: assignedTo,
          assignedToPhone: this.formAssignedPhone().trim() || undefined,
          problemDescription: maintenanceProblemFromDamage({
            notes: this.formRepairNotes(),
            damageDescription: record.damageDescription,
            quantityDamaged: record.quantityDamaged,
          }),
          estimatedCost:
            record.estimatedCost > 0 ? record.estimatedCost : undefined,
        })
        .subscribe((result) => {
          this.sendingToMaintenance.set(false);
          if (result.success) {
            this.closeRepairModal();
            this.equipmentService.refreshFromApi();
            this.showToastMessage(
              result.message ??
                'Sent to Maintenance. When the repair is completed, the item leaves Damaged / Lost and stock is updated.',
              'success',
            );
            return;
          }
          this.showToastMessage(
            result.message ?? 'Failed to send item to maintenance',
            'error',
          );
        });
      return;
    }

    this.sendingToMaintenance.set(true);

    this.maintenanceService
      .sendDamagedToMaintenance({
        equipmentAssetID,
        equipmentReturnID,
        assignedToName: assignedTo,
        assignedToPhone: this.formAssignedPhone().trim() || undefined,
        problemDescription: maintenanceProblemFromDamage({
          notes: this.formRepairNotes(),
          damageDescription: record.damageDescription,
          quantityDamaged: record.quantityDamaged,
        }),
        estimatedCost:
          record.estimatedCost > 0 ? record.estimatedCost : undefined,
      })
      .subscribe((result) => {
        this.sendingToMaintenance.set(false);
        if (result.success) {
          this.closeRepairModal();
          this.equipmentService.refreshFromApi();
          this.showToastMessage(
            result.message ??
              'Sent to Maintenance. Complete the job there to clear Damaged / Lost and restock.',
            'success',
          );
          return;
        }

        this.showToastMessage(
          result.message ?? 'Failed to send item to maintenance',
          'error',
        );
      });
  }

  canSendToMaintenance(record: DamagedEquipmentRecord): boolean {
    return (
      record.damageType === 'damaged' &&
      record.status !== 'resolved' &&
      record.status !== 'under-review'
    );
  }

  /** Lost items are written off — use resolve/write-off, not maintenance. */
  canMarkRepaired(record: DamagedEquipmentRecord): boolean {
    return false;
  }

  confirmWriteOff(): void {
    const record = this.selectedRecord();
    if (!record) {
      this.showToastMessage('Select a report first', 'error');
      return;
    }

    this.equipmentService
      .resolveDamagedRecordViaApi(record.id, 'written-off')
      .subscribe((result) => {
        this.closeWriteOffModal();
        if (result.success) {
          this.showToastMessage('Item written off successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to write off item',
            'error',
          );
        }
      });
  }

  writeOffLost(record: DamagedEquipmentRecord): void {
    if (
      !confirmAction(
        `Write off "${record.reportNumber}" (${record.equipment})? Lost units stay out of stock and leave Damaged / Lost.`,
      )
    ) {
      return;
    }

    this.equipmentService
      .resolveDamagedRecordViaApi(record.id, 'written-off')
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            'Lost item written off and removed from Damaged / Lost',
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to write off item',
            'error',
          );
        }
      });
  }

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  showSuccessToast(message: string): void {
    this.showToastMessage(message, 'success');
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.filteredRecords();
    if (!rows.length) {
      this.showToastMessage(
        'No damaged/lost records match the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `damaged-equipment-${new Date().toISOString().slice(0, 10)}`,
      rows.map((row) => ({
        'Report No.': row.reportNumber,
        Equipment: row.equipment,
        'Item Code': row.itemCode ?? '',
        'Item Number': row.assetNumber ?? '',
        Checkout: row.checkoutNumber ?? '',
        Type: row.damageType,
        'Qty Damaged': row.quantityDamaged ?? 0,
        'Qty Missing': row.quantityMissing ?? 0,
        Condition: row.conditionIn ?? '',
        Branch: row.branchName ?? '',
        Destination: row.destination ?? '',
        Status: row.status,
        'Reported Date': row.reportedDate,
        'Estimated Cost': row.estimatedCost,
      })),
    );
  }
}
